import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { CloseRoundIcon, LogoIcon, LogoOnlyIcon } from 'assets';
import { getLinkPreview } from 'services/api';
import { getUrl, groupImgUrl } from 'utilities/userImageURL';
import AvatarImg from 'pages/components/avatarImg';
import AdminButton from 'pages/components/button';
import { colors } from 'styles/colors';

interface UrlPreviewCardProps {
  url: string;
  onCloseLinkPreview?: () => void;
}

const UrlPreviewCard: React.FC<UrlPreviewCardProps> = ({
  url,
  onCloseLinkPreview
}) => {
  const [preview, setPreview] = useState<any>(null);
  const [hidden, setHidden] = useState(false);

  const baseUrl = useMemo(() => {
    try {
      const u = new URL(url);
      u.searchParams.delete("mode");
      return u.toString();
    } catch {
      return url;
    }
  }, [url]);

  useEffect(() => {
    let isMounted = true;

    const fetchPreview = async () => {
      try {
        const data = await getLinkPreview(url);
        if (isMounted) setPreview(data);
      } catch (err) {
        console.error('Failed to fetch preview for', url, err);
      }
    };

    fetchPreview();

    return () => {
      isMounted = false;
    };
  }, [url]);

  if (!preview || hidden) return null;

  return (
    <Box
      sx={{
        maxWidth: '100%',
        position: 'relative',
        bottom: '8px',
        left: '12px',
        right: '12px',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        margin: '12px 16px 0 0',
        overflow: 'hidden',
        background: '#efefef99'
      }}
      onClick={() => window.open(baseUrl, '_blank')}
    >
      <Box sx={{ display: 'flex', height: '96px', minWidth: 0 }}>
        {preview.imageurl && !preview.imageId ? (
          <Box
            component='img'
            src={preview.imageurl}
            alt='preview'
            sx={{
              width: 120,
              height: 96,
              objectFit: 'cover',
              flexShrink: 0,
              background: colors.text.lightShadeGray
            }}
          />
        ) : preview.imageId || preview.showInitial ? (
          <AvatarImg
            id='userImg'
            enableLetterImg={!preview.imageId || preview.showInitial}
            alt={preview.title}
            src={
              preview.isGroup
                ? preview.imageId
                  ? groupImgUrl(preview.imageId)
                  : ''
                : preview.imageId
                  ? getUrl(preview.imageId)
                  : ''
            }
            name={preview.title}
            nameStyle={{ fontSize: 40 }}
            width={120}
            height={96}
            style={{
              objectFit: 'cover',
              background: colors.text.lightShadeGray
            }}
            variant='square'
          />
        ) : null}

        <Box
          sx={{
            flex: 1,
            padding: '8px 12px',
            overflow: 'hidden',
            backgroundColor: '#efefef99'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.5
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: '#242424',
                lineHeight: '20px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1
              }}
            >
              {preview.title || url}
            </Typography>
            {!url.includes('mode=invite') && (
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setHidden(true);
                  onCloseLinkPreview?.();
                }}
                sx={{ p: '3px', flexShrink: 0 }}
              >
                <CloseRoundIcon fontSize='small' />
              </IconButton>
            )}
          </Box>

          {preview.description && (
            <Typography
              sx={{
                fontSize: 12,
                color: '#242424',
                lineHeight: '16px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mb: 1,
                mt: '-6px'
              }}
            >
              {preview.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {url.includes('4um.com') && <LogoOnlyIcon height={18} width={18} />}
            <Typography
              sx={{
                fontSize: 12,
                color: '#616161',
                lineHeight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textDecoration: 'underline'
              }}
            >
              {preview.baseurl || preview.url || url}
            </Typography>
          </Box>
        </Box>
      </Box>

      {url.includes('mode=invite') && (
        <AdminButton
          title='Accept'
          variant='contained'
          sx={{
            width: '100%',
            textTransform: 'none',
            borderRadius: '0 0 4px 4px',
            background: colors.background.myPollGreen
          }}
          onClick={(e) => {
            e.stopPropagation();
            window.open(url, '_blank');
          }}
        />
      )}
    </Box>
  );
};

export default UrlPreviewCard;
